import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { WhatsAppHeader } from "@/components/whatsapp/WhatsAppWorkspace";
import { Button } from "@/components/Button";
import {
  ListingViewToggle,
  ListingSummary,
  useListingView,
} from "@/components/whatsapp/WhatsAppUi";
import { Card } from "@/components/Card";
import { EmptyState } from "@/components/EmptyState";
import { Pagination } from "@/components/Pagination";
import { Input } from "@/components/Input";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { CampaignStatusBadge } from "@/components/campaigns/CampaignStatusBadge";
import { campaignsService, Campaign } from "@/services/campaigns.service";
import { useActiveAccount } from "@/hooks/useActiveAccount";
import { useDebounced } from "@/hooks/useDebounced";
import {
  Search,
  Plus,
  Trash2,
  Copy,
  RefreshCw,
  Send,
  Users,
  Calendar,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Megaphone,
  ArrowUpRight,
} from "lucide-react";
import clsx from "clsx";
import toast from "react-hot-toast";
import emptyCampaignsIllustration from "@/assets/illustrations/empty-campaigns.svg";
import { ListSkeleton } from '@/components/PageSkeletons';

const STATUS_FILTERS = [
  { value: "", label: "All" },
  { value: "DRAFT", label: "Draft" },
  { value: "SCHEDULED", label: "Scheduled" },
  { value: "RUNNING", label: "Running" },
  { value: "PAUSED", label: "Paused" },
  { value: "COMPLETED", label: "Completed" },
  { value: "CANCELLED", label: "Cancelled" },
  { value: "FAILED", label: "Failed" },
];

export default function CampaignsList() {
  const [data, setData] = useState<{
    items: Campaign[];
    total: number;
    page: number;
    limit: number;
  } | null>(null);
  const [view, setView] = useListingView("wa-campaigns-view", "list");
  const [loadError, setLoadError] = useState(false);
  const requestId = useRef(0);
  const [q, setQ] = useState("");
  // Debounce so we don't hit the backend on every keystroke — biggest single
  // source of the "slow campaigns list" the user reported.
  const debouncedQ = useDebounced(q, 350);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [del, setDel] = useState<Campaign | null>(null);
  const [syncingUid, setSyncingUid] = useState<string | null>(null);
  const [syncingAll, setSyncingAll] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const nav = useNavigate();
  const { active: activeAccount } = useActiveAccount();

  const refresh = async () => {
    const id = ++requestId.current;
    setRefreshing(true);
    setLoadError(false);
    try {
      const result = await campaignsService.list({
        q: debouncedQ || undefined,
        status: statusFilter || undefined,
        accountUid: activeAccount?.accountUid,
        page,
        limit: 24,
      });
      if (id === requestId.current) setData(result);
    } catch {
      if (id === requestId.current) setLoadError(true);
    } finally {
      if (id === requestId.current) setRefreshing(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQ, page, statusFilter, activeAccount?.accountUid]);

  // Reset page on filter / search change so users don't end up on an empty
  // page-3 after narrowing the result set.
  useEffect(() => {
    setPage(1);
  }, [debouncedQ, statusFilter]);

  const canDelete = (status: string) =>
    ["DRAFT", "CANCELLED", "COMPLETED", "FAILED"].includes(status);

  const syncOne = async (uid: string) => {
    if (syncingUid) return;
    setSyncingUid(uid);
    try {
      await campaignsService.sync(uid);
      toast.success("Stats refreshed");
      await refresh();
    } catch {
      /* interceptor surfaces */
    } finally {
      setSyncingUid(null);
    }
  };

  const syncAll = async () => {
    if (syncingAll) return;
    setSyncingAll(true);
    try {
      const r = await campaignsService.syncAll();
      toast.success(
        `Synced ${r.updated} campaign${r.updated === 1 ? "" : "s"}`,
      );
      await refresh();
    } catch {
      /* interceptor surfaces */
    } finally {
      setSyncingAll(false);
    }
  };

  // Top-of-list rollup numbers across the visible page.
  const rollup = data
    ? data.items.reduce(
        (acc, c) => ({
          recipients: acc.recipients + c.totalRecipients,
          sent: acc.sent + c.sent,
          delivered: acc.delivered + c.delivered,
          read: acc.read + c.read,
          failed: acc.failed + c.failed,
        }),
        { recipients: 0, sent: 0, delivered: 0, read: 0, failed: 0 },
      )
    : null;

  const actions = (c: Campaign) => (
    <div className="flex items-center gap-1">
      <Button
        size="sm"
        variant="ghost"
        title="Refresh delivery stats"
        aria-label={`Refresh ${c.name}`}
        onClick={() => syncOne(c.campaignUid)}
        disabled={syncingUid === c.campaignUid}
        className="!px-2"
      >
        <RefreshCw
          size={15}
          className={syncingUid === c.campaignUid ? "animate-spin" : ""}
        />
      </Button>
      <Button
        size="sm"
        variant="ghost"
        title="Duplicate as a draft"
        aria-label={`Duplicate ${c.name}`}
        className="!px-2"
        onClick={async () => {
          try {
            const cloned = await campaignsService.clone(c.campaignUid);
            toast.success(`Cloned as "${cloned.name}"`);
            nav(`/campaigns/${cloned.campaignUid}`);
          } catch {
            /* interceptor surfaces */
          }
        }}
      >
        <Copy size={15} />
      </Button>
      {canDelete(c.status) && (
        <Button
          size="sm"
          variant="ghost"
          title="Delete campaign"
          aria-label={`Delete ${c.name}`}
          className="!px-2 hover:!text-danger"
          onClick={() => setDel(c)}
        >
          <Trash2 size={15} />
        </Button>
      )}
    </div>
  );
  const progress = (c: Campaign) => (
    <div>
      <div className="mb-2 flex justify-between gap-2 text-xs text-text-muted">
        <span>
          {c.sent.toLocaleString()} of {c.totalRecipients.toLocaleString()} sent
        </span>
        <span>
          {c.totalRecipients
            ? Math.min(100, Math.round((c.sent / c.totalRecipients) * 100))
            : 0}
          %
        </span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-surface-alt">
        <div
          className="h-full rounded-full bg-primary"
          style={{
            width: `${c.totalRecipients ? Math.min(100, (c.sent / c.totalRecipients) * 100) : 0}%`,
          }}
        />
      </div>
    </div>
  );
  const clearFilters = () => {
    setQ("");
    setStatusFilter("");
    setPage(1);
  };

  return (
    <>
      <WhatsAppHeader
        section="campaigns"
        title="Campaigns"
        description="Reach the right people. Follow every message from send to read."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="secondary"
              onClick={syncAll}
              loading={syncingAll}
              iconLeft={<RefreshCw size={16} />}
            >
              Sync results
            </Button>
            <Button
              onClick={() => nav("/campaigns/new")}
              iconLeft={<Plus size={16} />}
            >
              Create campaign
            </Button>
          </div>
        }
      />
      <div className="mx-auto w-full max-w-[1680px] px-4 py-6 sm:px-7 sm:py-8">
        <ListingSummary
          items={[
            {
              label: "Campaigns",
              value: data?.total.toLocaleString() ?? "—",
              detail:
                q || statusFilter ? "Matching your filters" : "In this account",
              icon: <Megaphone size={18} />,
            },
            {
              label: "Messages sent",
              value: rollup?.sent.toLocaleString() ?? "—",
              detail: "Across campaigns on this page",
              icon: <Send size={18} />,
            },
            {
              label: "Delivered",
              value: rollup?.delivered.toLocaleString() ?? "—",
              detail: "Confirmed delivery · this page",
              icon: <CheckCircle size={18} />,
            },
            {
              label: "Read",
              value: rollup?.read.toLocaleString() ?? "—",
              detail: "Confirmed reads · this page",
              icon: <TrendingUp size={18} />,
            },
          ]}
        />
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold tracking-tight text-text-primary">
              Your campaigns
            </h2>
            <p className="mt-1 text-sm text-text-muted">
              {activeAccount?.businessName || "WhatsApp campaigns"}
              {activeAccount?.displayPhoneNumber
                ? ` · ${activeAccount.displayPhoneNumber}`
                : ""}
            </p>
          </div>
          <span className="text-xs text-text-muted">
            {refreshing ? "Refreshing…" : `${data?.total ?? 0} campaigns`}
          </span>
        </div>
        <div className="mb-6 rounded-2xl border border-border bg-surface px-4 pt-4 sm:px-5">
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <Input
                aria-label="Search campaigns"
                iconLeft={<Search size={17} />}
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setPage(1);
                }}
                placeholder="Search your campaigns…"
              />
            </div>
            <Button
              aria-label="Refresh campaigns"
              title="Refresh campaigns"
              variant="secondary"
              className="!px-3"
              onClick={refresh}
              disabled={refreshing}
            >
              <RefreshCw
                size={16}
                className={refreshing ? "animate-spin" : ""}
              />
            </Button>
            <ListingViewToggle value={view} onChange={setView} />
          </div>
          <div
            className="mt-4 flex gap-5 overflow-x-auto"
            role="group"
            aria-label="Campaign status"
          >
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.value}
                onClick={() => {
                  setStatusFilter(f.value);
                  setPage(1);
                }}
                aria-pressed={statusFilter === f.value}
                className={clsx(
                  "shrink-0 border-b-2 pb-3 text-sm font-medium transition-colors",
                  statusFilter === f.value
                    ? "border-primary text-primary"
                    : "border-transparent text-text-muted hover:text-text-primary",
                )}
              >
                {f.label === "All" ? "All campaigns" : f.label}
              </button>
            ))}
          </div>
        </div>
        {loadError ? (
          <Card className="text-center">
            <p className="mb-3 text-sm text-text-secondary">
              We couldn't load your campaigns.
            </p>
            <Button onClick={refresh} variant="secondary">
              Try again
            </Button>
          </Card>
        ) : !data ? (
          <ListSkeleton rows={6} columns={4} />
        ) : data.items.length === 0 ? (
          <Card className="py-10">
            <EmptyState
              illustration={emptyCampaignsIllustration}
              title={
                q || statusFilter
                  ? "No matching campaigns"
                  : "Make your first connection"
              }
              description={
                q || statusFilter
                  ? "Try a different name or status to find your campaign."
                  : "Choose a message, select your audience and send something worth opening."
              }
              action={
                <Button
                  onClick={
                    q || statusFilter
                      ? clearFilters
                      : () => nav("/campaigns/new")
                  }
                >
                  {q || statusFilter ? "Clear filters" : "Create campaign"}
                </Button>
              }
            />
          </Card>
        ) : (
          <>
            {view === "grid" ? (
              <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
                {data.items.map((c) => (
                  <article
                    key={c.campaignUid}
                    className="flex min-w-0 flex-col rounded-2xl border border-border bg-surface transition-shadow hover:shadow-md"
                  >
                    <div className="p-5">
                      <div className="mb-5 flex items-center justify-between gap-2">
                        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-soft text-primary">
                          <Megaphone size={20} />
                        </span>
                        <CampaignStatusBadge status={c.status} />
                      </div>
                      <Link
                        to={`/campaigns/${c.campaignUid}`}
                        className="block truncate text-base font-semibold text-text-primary hover:text-primary"
                        title={c.name}
                      >
                        {c.name}
                      </Link>
                      <p className="mt-1.5 flex items-center gap-1.5 text-xs text-text-muted">
                        <Users size={13} />
                        {c.totalRecipients
                          ? `${c.totalRecipients.toLocaleString()} recipients`
                          : "Audience prepared at launch"}
                      </p>
                      <div className="my-5">{progress(c)}</div>
                      <div className="grid grid-cols-3 divide-x divide-border border-t border-border pt-4">
                        {[
                          { label: "Delivered", value: c.delivered },
                          { label: "Read", value: c.read },
                          { label: "Failed", value: c.failed },
                        ].map((m) => (
                          <div key={m.label} className="px-3 first:pl-0">
                            <div className="text-xs text-text-muted">
                              {m.label}
                            </div>
                            <div
                              className={clsx(
                                "mt-1 text-lg font-semibold tabular-nums",
                                m.label === "Failed" && m.value
                                  ? "text-danger"
                                  : "text-text-primary",
                              )}
                            >
                              {m.value.toLocaleString()}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="mt-auto flex flex-wrap items-center justify-between gap-2 border-t border-border px-4 py-3">
                      <Link
                        className="inline-flex items-center gap-1 text-xs font-semibold text-text-secondary hover:text-primary"
                        to={`/campaigns/${c.campaignUid}`}
                      >
                        View campaign
                        <ArrowUpRight size={14} />
                      </Link>
                      {actions(c)}
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="overflow-x-auto rounded-2xl border border-border bg-surface">
                <table className="w-full min-w-[880px] text-left text-sm">
                  <thead className="border-b border-border bg-surface-alt/50 text-xs text-text-muted">
                    <tr>
                      {[
                        "Campaign",
                        "Status",
                        "Delivery progress",
                        "Delivered",
                        "Read",
                        "Actions",
                      ].map((h) => (
                        <th
                          key={h}
                          className="px-5 py-4 font-medium last:text-right"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {data.items.map((c) => (
                      <tr
                        key={c.campaignUid}
                        className="hover:bg-surface-alt/40"
                      >
                        <td className="max-w-[340px] px-5 py-5">
                          <div className="flex items-center gap-3">
                            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-soft text-primary">
                              <Megaphone size={18} />
                            </span>
                            <div className="min-w-0">
                              <Link
                                className="block truncate font-semibold text-text-primary hover:text-primary"
                                title={c.name}
                                to={`/campaigns/${c.campaignUid}`}
                              >
                                {c.name}
                              </Link>
                              <span className="mt-1 flex items-center gap-1.5 text-xs text-text-muted">
                                <Calendar size={12} />
                                {new Date(
                                  c.scheduledAt || c.createdAt,
                                ).toLocaleDateString(undefined, {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                })}
                                {c.failed > 0 && (
                                  <span className="ml-2 flex items-center gap-1 text-danger">
                                    <AlertCircle size={12} />
                                    {c.failed} failed
                                  </span>
                                )}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <CampaignStatusBadge status={c.status} />
                        </td>
                        <td className="min-w-[180px] px-5 py-4">
                          {progress(c)}
                        </td>
                        <td className="px-5 py-4 font-medium tabular-nums text-text-primary">
                          {c.delivered.toLocaleString()}
                        </td>
                        <td className="px-5 py-4 font-medium tabular-nums text-text-primary">
                          {c.read.toLocaleString()}
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex justify-end">{actions(c)}</div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <Pagination
              page={data.page}
              total={data.total}
              limit={data.limit}
              onChange={setPage}
            />
          </>
        )}
      </div>
      <ConfirmDialog
        open={!!del}
        title="Delete campaign?"
        message={`Delete "${del?.name}"? Recipient logs are kept for audit.`}
        danger
        onConfirm={async () => {
          await campaignsService.remove(del!.campaignUid);
          toast.success("Deleted");
          setDel(null);
          refresh();
        }}
        onCancel={() => setDel(null)}
      />
    </>
  );
}
